import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '~shared/shadcn/calendar';
import { useIsMobile } from '~shared/shadcn/hooks/use-mobile';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

interface DatePickerWithRangeProps {
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  className?: string;
  triggerClassName?: string;
}

export const DatePickerWithRange = ({
  date,
  setDate,
  className,
  triggerClassName,
}: DatePickerWithRangeProps) => {
  const isMobile = useIsMobile();

  // Pass range selection through as-is. Custom rewrite logic previously
  // cleared `to` on the second click, which blocked report downloads.
  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    setDate(
      dateRange ?? {
        from: undefined,
        to: undefined,
      },
    );
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant='outline'
            className={cn(
              'h-9 w-full min-w-0 justify-start border-slate-200 bg-white text-left text-sm font-normal transition-all duration-200 hover:border-slate-300 hover:bg-slate-50 sm:w-max',
              !date && 'text-muted-foreground',
              triggerClassName,
            )}
          >
            <CalendarIcon className='mr-2 size-4 text-slate-500' />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'MMM dd')} -{' '}
                  {format(date.to, 'MMM dd, yyyy')}
                </>
              ) : (
                format(date.from, 'MMM dd, yyyy')
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align={isMobile ? 'center' : 'start'}
          collisionPadding={12}
          className='w-auto max-w-[calc(100vw-1.5rem)] border-slate-200 p-0 shadow-xl'
        >
          <Calendar
            // initialFocus
            mode='range'
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleDateRangeChange}
            numberOfMonths={isMobile ? 1 : 2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
